import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/natConfiguration/natConfiguration.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import { newNatConfiguration } from 'routes/NatConfiguration/NatConfiguration.types';
import { adaptToCreate } from 'data/natConfiguration/natConfiguration.adapter';
import * as types from 'data/natConfiguration/natConfiguration.types';
import { NatConfiguration } from 'data/natConfiguration/natConfiguration.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import NatConfigurationForm from './components/NatConfigurationForm';
import natConfigurationTemplate from '../../templates/index';
import { adaptTemplateVisibilityMode } from '../../utils/NatConfiguration.adapter';
import { getAuditDetailsToSubmit } from '../../utils/NatConfigurationDataToSubmit';
import '../../styles.scss';

type RouterParams = {
  history: string
}

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.NatConfiguration
}

const mapState = (state: RootState) => ({
  natConfigurationTemplateDef: adaptTemplateVisibilityMode(natConfigurationTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  natConfigurationTemplate: adaptTemplateVisibilityMode(natConfigurationTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  createNatConfiguration: actions.createNatConfiguration,
  goToNatConfigurationList: actions.goToNatConfigurationList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const NatConfigurationCreate = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  natConfigurationTemplateDef,
  goToNatConfigurationList,
  createNatConfiguration,
}: Props): JSX.Element => {
  const portRangeRef = useRef<Ref>(null);
  const mainRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<NatConfiguration>(cloneDeep(newNatConfiguration));
  const [template, setTemplate] = useState(natConfigurationTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateNatConfigurationPayload = () => {
    if (mainRef?.current === undefined || portRangeRef?.current === null) return null;

    if ((!mainRef?.current?.isValid(true, true)) && (!portRangeRef?.current?.isValid(true, true))) {
      return null;
    }

    const mainData = mainRef.current?.getValues();
    const portRangeData = portRangeRef.current.getValues();
    const data = { ...mainData, ...portRangeData };
    const payload = {
      data: adaptToCreate(data),
      auditDetails: getAuditDetailsToSubmit(data, null, isAuditEnabled, userName, userId, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateNatConfigurationPayload();
    if (payload) {
      createNatConfiguration(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToNatConfigurationList(history);

  const renderForm = (section: string) => {
    if (formData && template) {
      return (
        <NatConfigurationForm
          ref={section === 'private' ? mainRef : portRangeRef}
          data={formData}
          template={template}
          section={section}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('natConfiguration.create.newNatConfiguration'))}
        <h2 className="natConfigurationH2">{t('natConfiguration.section.private')}</h2>
        {renderForm('private')}
        <h2 className="natConfigurationH2">{t('natConfiguration.section.public')}</h2>
        {renderForm('public')}
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(NatConfigurationCreate));
